/**
 * 手牌结束后的结算浮层 + 「全员确认才开下一局」。
 *
 * ── 为什么不再 3 秒自动开下一手 ─────────────────────────────
 * 三秒看不清谁赢了、赢的是什么牌型，牌一收就已经在发下一手。改成：
 * 在座真人全部点「继续」就立刻开下一手，AI 自动算已确认，断线 / 坐出的人不进名单，
 * 房主可强制开始，90 秒兜底。
 *
 * ── 三条不能动的交互约束 ─────────────────────────────────────
 *
 * 1. **必须说出在等谁**。只给一个不动的「2/4 已确认」，谁也不知道该催谁，
 *    结果是一桌人盯着一个数字干等。
 *
 * 2. **不做紧迫的倒计时**。90 秒是防「等一个已经走了、但系统以为还在的人」，
 *    不是行动计时。所以剩余时间按 10 秒粒度粗略地说一句，不逐秒跳（见 `autoStartText`）。
 *
 * 3. **没有摊牌的局面照样出结果**。全下跑马、弃牌收场都有赢家和金额，只是没有牌型。
 *    少画这一屏，玩家只能靠筹码数字倒推刚才发生了什么。
 *
 * ★ 牌型文案一律由 `formatHand` 产出（经 `handLabel` 转发），UI 不拼字符串（红线 #3 同源）。
 * ★ 红线 #37：`readyForNext` 不带座位，座位由连接身份解析。
 * ★ 玩家点「继续」后本机立即收起；未确认者则在收到 `handStarted` 或整局战报时收起。
 */
import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { PlayerView } from '@poker/shared'

import { IconCheck } from '../icons/index.js'
import { Card } from '../table/Card.js'
import { selectNetView, useNetStore } from '../transport/applyMessage.js'

import { autoStartText, handEndOverlayOpen, handResultModel, readyModel, readyWaitText } from './readyDerive.js'
import { useRoomLink } from './roomLink.js'
import { settlementOf } from './topUpDerive.js'
import { useTickingNow } from './useNow.js'
import './room.css'

/**
 * 剩余时间文案的刷新间隔。
 *
 * 文案本身是 10 秒粒度的（「约 40 秒后自动继续」），5 秒一跳就不会出现
 * 「明明该跳了却还停着」的滞后感，同时离「每帧」还差三个数量级 —— 红线 #48 管的是后者。
 */
const TICK_MS = 5000

export interface HandResultOverlayProps {
  /** 覆盖 store 里的视图（回放 / storybook / 单测用）；省略就订阅 store。 */
  readonly view?: PlayerView | null
  /** 当前玩家确认看完后通知房间层，随后才能露出补码 / 等待室。 */
  readonly onConfirmed?: (handId: string) => void
}

export function HandResultOverlay({ view: viewProp, onConfirmed }: HandResultOverlayProps = {}): ReactNode {
  const storeView = useNetStore(selectNetView)
  const view = viewProp === undefined ? storeView : viewProp
  const link = useRoomLink()

  const ready = readyModel(view)
  const result = handResultModel(view)
  // 整局战报已经包含这一手的结果，两张结果卡不应同时出现。
  // 即使断线重连的快照里意外同时带了 readyState 和 settlement，也以整局战报为准。
  const open = handEndOverlayOpen(view, settlementOf(view) !== null)
  const now = useTickingNow(open, TICK_MS)
  const handNo = result?.handNo ?? null
  const [dismissedHand, setDismissedHand] = useState<number | null>(null)

  useEffect(() => {
    if (!open) setDismissedHand(null)
  }, [open])

  const confirm = useCallback((): void => {
    // 「继续」代表这一位玩家已经看完：本机立刻收起结果，不必再等服务器回包或下一手开局。
    setDismissedHand(handNo)
    if (view?.hand !== null && view?.hand !== undefined) onConfirmed?.(view.hand.handId)
    // ★ 红线 #37：不带座位
    link.send({ type: 'readyForNext' })
  }, [handNo, link, onConfirmed, view?.hand])

  const force = useCallback((): void => {
    link.send({ type: 'forceNextHand' })
  }, [link])

  if (!open || result === null || dismissedHand === result.handNo) return null

  const waiting = ready.waitingNames.length > 0
  // ★ 确认齐了仍要等 `interHandDelayMs` 的最短展示时长 —— 这里绝不画一个停在 0 的数字
  const timeText = waiting ? autoStartText(ready.deadline - now) : '马上开始'

  return (
    <div className="handend" role="region" aria-label="本手结果">
      <div className="handend__card">
        <div className="handend__head">
          <span>第 {result.handNo} 手</span>
        </div>
        {result.showdown.length > 0 ? (
          <div className="handend__comparison">
            <div className="handend__board" aria-label="公共牌">
              {result.board.map((card, index) => (
                <Card key={index} card={card} faceUp size="board" width={28} height={40} />
              ))}
            </div>
            <ol className="handend__showdown">
              {result.showdown.map((line) => (
                <li key={line.key} className="handend__showdown-row">
                  <span className="handend__place" data-first={line.place === 1 ? 'yes' : 'no'}>
                    {line.place === null ? '—' : `#${line.place}`}
                  </span>
                  <span className="handend__cards" aria-label={`${line.nickname} 的底牌`}>
                    {line.cards === null
                      ? [0, 1].map((index) => (
                          <Card key={index} card={null} faceUp={false} size="board" width={26} height={36} />
                        ))
                      : line.cards.map((card, index) => (
                          <Card key={index} card={card} faceUp size="board" width={26} height={36} />
                        ))}
                  </span>
                  <span className="handend__showdown-info">
                    <strong title={line.nickname}>{line.nickname}</strong>
                    {line.handText !== null && <small>{line.handText}</small>}
                  </span>
                  {line.amount !== null && <span className="handend__showdown-award num">+{line.amount}</span>}
                </li>
              ))}
            </ol>
          </div>
        ) : (
          <ul className="handend__wins" role="list">
            {result.lines.map((l) => (
              <li key={l.key} className="handend__win">
                <span className="handend__who">{l.nickname}</span>
                <span className="handend__amt num">{l.amount}</span>
                {/* 没摊牌就没有牌型：绝不编一个出来（红线 #3） */}
                {l.handText !== null && <span className="handend__hand">{l.handText}</span>}
              </li>
            ))}
          </ul>
        )}

        <div className="handend__foot">
          {ready.mine === 'ready' ? (
            // 点过就是点过：不给取消。「我看完了」这件事没有反悔的意义，
            // 而一颗能反复切换的按钮会让别人的等待名单来回跳
            <span className="handend__done">
              <IconCheck size={16} />
              已确认
            </span>
          ) : ready.mine === 'waiting' ? (
            <button type="button" className="btn btn--cta pressable handend__go" onClick={confirm}>
              继续
            </button>
          ) : null}

          <span className="handend__wait">
            <span className="handend__count">{readyWaitText(ready)}</span>
            <span className="handend__timer">{timeText}</span>
          </span>
        </div>

        {/* 房主的逃生口：不等还没点的人。只在真的还在等人时出现 */}
        {ready.canForce && waiting && (
          <button type="button" className="btn btn--quiet handend__force" onClick={force}>
            直接开始
          </button>
        )}
      </div>
    </div>
  )
}

export default HandResultOverlay
